import React, {useEffect, useState, useMemo, useCallback} from 'react';
import Modal from "@components/modal/modal.component";
import {useTranslation} from "react-i18next";
import {Controller, useForm} from "react-hook-form";
import Form from "@components/form";
import {IonAlert, IonCol, IonRow, IonSelectOption} from "@ionic/react";
import TimeInput from "@components/form/input/time.component";
import {StyledButton, StyledRedButton} from "@components/button/button.style";
import {AvailabilityForm} from "@models/availability";
import {StyledIonSelect} from "@components/form/input/select.style";
import {StyledIonLabel} from "@components/form/input/label.style";
import {getRepeatTypeOptions, getTimeDurationOptions, removeAvailability, updateAvailability} from "@services/calendar/availability.service";
import {useToast} from "@context/toast.context";
import {Availability} from "@models/calendar";
import moment from "moment/moment";
import DateInput from "@components/form/input/date.component";
import PreferredPlacesOfServiceListModal from "@app/availabilityPage/modals/preferredPlacesOfServiceListModal.component";
import {StyledTextHref} from "@components/text/text.styles";
import {ReactComponent as CloseIcon} from "@assets/images/e-remove.svg";
import {canEditAvailability, canEditPreferredPlaces, getAvailabilityErrorMessage} from "@services/calendar/availability.service";
import {useAutoEndTime} from "@hooks/calendar/useAutoEndTime";
import {useRepeatEndDate} from "@hooks/calendar/useRepeatEndDate";
import {useUnsavedChangesGuard} from "@hooks/calendar/useUnsavedChangesGuard";
import {usePreferredPlacesTracking} from "@hooks/calendar/usePreferredPlacesTracking";

type AvailabilityEditModalProps = {
    isOpen: boolean,
    onClose: () => void,
    onDone: () => void,
    availability: Availability,
}

const AvailabilityEditModal: React.FC<AvailabilityEditModalProps> = ({isOpen, onClose, onDone, availability}: AvailabilityEditModalProps) => {
    const {t} = useTranslation();
    const {showToast} = useToast();

    const {control, handleSubmit, errors, reset, setValue, watch, register, formState} = useForm<AvailabilityForm>(
        {
            mode: 'onSubmit',
            defaultValues: {
                duration: availability.attributes.duration,
                startTime: availability.attributes.originalStartTime,
                endTime: availability.attributes.originalEndTime,
                repeatType: availability.attributes.repeatType,
                repeatEndDate: availability.attributes.repeatEndDate,
                preferredPlacesOfService: availability.attributes.preferredPlacesOfService?.map(p => p.id) || [],
                agreementForOuterPlaces: availability.attributes.agreementForOuterPlaces,
            },
        }
    );

    const duration = watch('duration');
    const startTime = watch('startTime');
    const repeatType = watch('repeatType');

    const [showPreferredPlacesOfServiceModal, setShowPreferredPlacesOfServiceModal] = useState(false);
    const [showRemoveAvailabilityAlert, setShowRemoveAvailabilityAlert] = useState(false);

    const {
        preferredPlacesModified,
        setPreferredPlacesModified,
        initialPreferredPlaces,
        setInitialPreferredPlaces,
        handlePreferredPlacesChange,
    } = usePreferredPlacesTracking();

    const {
        showCloseConfirmAlert,
        setShowCloseConfirmAlert,
        allowDismiss,
        setAllowDismiss,
        handleCanDismiss,
        handleDidDismiss,
        confirmClose,
        handleCloseButtonClick,
    } = useUnsavedChangesGuard({
        isOpen,
        formState,
        preferredPlacesModified,
        onClose,
    });

    useAutoEndTime({duration, startTime, setValue});

    const maxRepeatEndDate = useRepeatEndDate({repeatType, startTime, watch, setValue});

    const canEdit = useMemo(
        () => canEditAvailability(availability.startTime, 48),
        [availability.startTime]
    );

    const canEditPreferredPlacesOfService = useMemo(
        () => canEditPreferredPlaces(availability.startTime),
        [availability.startTime]
    );

    useEffect(() => {
        if (isOpen && availability) {
            const preferredPlacesIds = availability.attributes.preferredPlacesOfService?.map(p => p.id) || [];
            const agreementForOuter = availability.attributes.agreementForOuterPlaces || false;

            reset({
                duration: availability.attributes.duration,
                startTime: availability.attributes.originalStartTime,
                endTime: availability.attributes.originalEndTime,
                repeatType: availability.attributes.repeatType,
                repeatEndDate: availability.attributes.repeatEndDate,
                preferredPlacesOfService: preferredPlacesIds,
                agreementForOuterPlaces: agreementForOuter,
            });

            setInitialPreferredPlaces({ids: preferredPlacesIds, agreement: agreementForOuter});
            setPreferredPlacesModified(false);
        }
    }, [isOpen, availability, reset, setInitialPreferredPlaces, setPreferredPlacesModified]);

    useEffect(() => {
        register('preferredPlacesOfService');
        register('agreementForOuterPlaces');
    }, [register]);

    const onSubmit = useCallback(async (data: AvailabilityForm) => {
        if (!canEdit && !canEditPreferredPlacesOfService) {
            return;
        }

        try {
            await updateAvailability(availability.id, data, availability.attributes.originalDate);

            showToast({
                message: t('availability.edited'),
                type: 'success',
                duration: 3000,
            });

            setAllowDismiss(true);
            onDone();
            onClose();
        } catch (error) {
            const message = getAvailabilityErrorMessage(error, true);

            showToast({
                message: t(message),
                type: 'danger',
                duration: 3000,
            });

            console.error(error);
        }
    }, [canEdit, canEditPreferredPlacesOfService, availability.id, availability.attributes.originalDate, showToast, t, setAllowDismiss, onDone, onClose]);

    const handleAvailabilityRemove = useCallback(async (removeNextRepeats: boolean = false) => {
        try {
            await removeAvailability(availability.id, availability.attributes.originalDate, removeNextRepeats);

            showToast({
                message: t('availability.removed'),
                type: 'success',
                duration: 3000,
            });

            setAllowDismiss(true);
            onDone();
            onClose();
        } catch (error) {
            showToast({
                message: t('availability.error.remove'),
                type: 'danger',
                duration: 3000,
            });

            console.error(error);
        }
    }, [availability.id, availability.attributes.originalDate, showToast, t, setAllowDismiss, onDone, onClose]);

    return (
        <Modal
            initialBreakpoint={0.6}
            breakpoints={[0, 0.6]}
            isOpen={isOpen}
            onDidDismiss={handleDidDismiss}
            canDismiss={handleCanDismiss}
            disableClose={true}
        >
            <CloseIcon className="close" onClick={handleCloseButtonClick}/>
            <IonAlert
                isOpen={showRemoveAvailabilityAlert}
                onDidDismiss={() => setShowRemoveAvailabilityAlert(false)}
                header={t('availability.remove.title')}
                buttons={[
                    {
                        cssClass: 'alert-small-font',
                        text: t('availability.remove.nextRepeats'),
                        handler: async () => {
                            await handleAvailabilityRemove(true);
                        }
                    },
                    {
                        text: t('availability.remove.onlyThisDay'),
                        handler: async () => {
                            await handleAvailabilityRemove();
                        }
                    },
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            setShowRemoveAvailabilityAlert(false);
                        }
                    },
                ]}
            />
            <IonAlert
                isOpen={showCloseConfirmAlert}
                onDidDismiss={() => setShowCloseConfirmAlert(false)}
                header={t('availability.unsavedChanges.alert')}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            setShowCloseConfirmAlert(false);
                        }
                    },
                    {
                        text: t('common.ok'),
                        handler: () => {
                            confirmClose();
                        }
                    },
                ]}
            />
            <PreferredPlacesOfServiceListModal
                isOpen={showPreferredPlacesOfServiceModal}
                onClose={() => setShowPreferredPlacesOfServiceModal(false)}
                preSelected={watch('preferredPlacesOfService')}
                isAgreementForOuterPlaces={watch('agreementForOuterPlaces')}
                setValue={(selectedPlaces, agreementForOuterPlaces) =>
                    handlePreferredPlacesChange(selectedPlaces, agreementForOuterPlaces, setValue)
                }
                disabled={!canEditPreferredPlacesOfService}
            />
            <Form.Container onSubmit={handleSubmit(onSubmit)}>
                <IonRow>
                    <IonCol size="12" className="label" style={{margin: "10px 0"}}>
                        {t('availability.editAvailabilityTitle')} - {moment(availability.attributes.originalDate).format('DD-MM-YYYY')}
                    </IonCol>
                </IonRow>
                <IonRow>
                    <IonCol size="12" className="label">
                        <Controller
                            name="duration"
                            control={control}
                            rules={{required: true}}
                            render={(field) => (
                                <StyledIonSelect
                                    placeholder={t('availability.selectDurationOption')}
                                    onIonChange={(e) => field.onChange(e.detail.value)}
                                    value={field.value}
                                    className={errors.duration ? 'error' : ''}
                                    disabled={!canEdit}
                                >
                                    {
                                        getTimeDurationOptions().map((timeDurationOption) => {
                                            return <IonSelectOption
                                                key={timeDurationOption.value}
                                                value={timeDurationOption.value}>{timeDurationOption.label}</IonSelectOption>
                                        })
                                    }
                                </StyledIonSelect>
                            )}
                        />
                    </IonCol>
                </IonRow>
                <IonRow>
                    <IonCol size="3" className="label">
                        <Controller
                            name="startTime"
                            control={control}
                            rules={{
                                required: true,
                                validate: (value) => {
                                    if (!value) return false;

                                    const now = moment();
                                    const selected = moment(value);

                                    if (selected.isBefore(now) && !canEditPreferredPlacesOfService) {
                                        showToast({
                                            message: t('availability.error.hoursForAvailabilityEditExceeded'),
                                            type: 'danger',
                                            duration: 3000,
                                        });

                                        return false;
                                    }

                                    return true;
                                },
                            }}
                            render={(field) => (
                                <TimeInput
                                    keepContentsMounted={true}
                                    cancelText={t("common.dateCancel")}
                                    doneText={t("common.dateDone")}
                                    placeholder={t("availability.startTime.placeholder")}
                                    onChange={(date) => field.onChange(date)}
                                    value={field.value}
                                    disabled={!canEdit}
                                    className={!canEdit ? "disabled" : ""}
                                />
                            )}
                        />
                    </IonCol>
                    <IonCol size="3" className="label">
                        <Controller
                            name="endTime"
                            control={control}
                            rules={{required: true}}
                            render={(field) => (
                                <TimeInput
                                    disabled={true}
                                    keepContentsMounted={true}
                                    cancelText={t("common.dateCancel")}
                                    doneText={t("common.dateDone")}
                                    placeholder={t("availability.endTime.placeholder")}
                                    onChange={(date) => field.onChange(date)}
                                    value={field.value}
                                    className="disabled"
                                />
                            )}
                        />
                    </IonCol>
                    <IonCol size="6" className="label">
                        <Controller
                            name="repeatType"
                            control={control}
                            rules={{required: true}}
                            render={(field) => (
                                <StyledIonSelect
                                    placeholder={t('availability.selectRepeatTypeOption')}
                                    onIonChange={(e) => field.onChange(e.detail.value)}
                                    onIonBlur={field.onBlur}
                                    value={field.value}
                                    className={errors.repeatType ? 'error' : ''}
                                    disabled={!canEdit}
                                >
                                    {
                                        getRepeatTypeOptions().map((repeatOption) => {
                                            return <IonSelectOption
                                                key={repeatOption.key}
                                                value={repeatOption.key}>{t(repeatOption.label)}</IonSelectOption>
                                        })
                                    }
                                </StyledIonSelect>
                            )}
                        />
                    </IonCol>
                </IonRow>
                {
                    repeatType && repeatType !== 'DONT_REPEAT' ? (
                        <>
                            <IonRow className="mt-8">
                                <IonCol size="12">
                                    <StyledIonLabel>
                                        {t('availability.repeatEndDate.label')}
                                    </StyledIonLabel>
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="label">
                                    <Controller
                                        name={'repeatEndDate'}
                                        control={control}
                                        rules={{required: false}}
                                        render={(field) => (
                                            <DateInput
                                                presentation="date"
                                                format="DD.MM.YYYY"
                                                onChange={(value) => field.onChange(moment(value).format('YYYY-MM-DD'))}
                                                min={moment(startTime).add(1, 'day').format('YYYY-MM-DD')}
                                                max={maxRepeatEndDate}
                                                cancelText={t("common.dateCancel")}
                                                doneText={t("common.dateDone")}
                                                placeholder={t("common.select")}
                                                value={field.value}
                                                disabled={!canEdit}
                                                className={!canEdit ? "disabled" : ""}
                                            />
                                        )}
                                    />
                                </IonCol>
                            </IonRow>
                        </>
                    ) : <></>
                }
                <IonRow>
                    <IonCol size="12">
                        <StyledTextHref className="full-width" onClick={() => setShowPreferredPlacesOfServiceModal(true)}>{t('availability.preferredPlacesOfService.label')}</StyledTextHref>
                    </IonCol>
                </IonRow>
                <IonRow style={{gap: "5px"}}>
                    {
                        canEdit && (
                            <StyledRedButton type="button"
                                             style={{flex: "1"}}
                                             onClick={async () => {
                                                 if (repeatType && repeatType !== 'DONT_REPEAT') {
                                                     setShowRemoveAvailabilityAlert(true);
                                                 } else {
                                                     await handleAvailabilityRemove();
                                                 }
                                             }}
                            >
                                {t('availability.remove.button')}
                            </StyledRedButton>
                        )
                    }
                    {
                        (canEdit || canEditPreferredPlacesOfService) && (
                            <StyledButton type="submit" style={{flex: "2"}}>{t('availability.update.button')}</StyledButton>
                        )
                    }
                </IonRow>
            </Form.Container>
        </Modal>
    );
};

export default AvailabilityEditModal;